import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { SmallBody } from "helpers/TypographyComponents";
import { formatCustomRoleId } from "helpers/utils";
import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { updateUser, updateUserRole } from "store/adminPanelSlice";
import { updateUserData } from "store/settingsStore";
import { useAppDispatch } from "store/store";

import type { PossibleUserRole } from "hooks/usePossibleUserRoles";

const formStyle = { display: "flex", flexDirection: "column", gap: "2rem" } as CSSProperties;

export interface IDefaultUserValues {
    firstname: string;
    lastname: string;
    role: PossibleUserRole;
    jobTitle: string;
}

/**
 * Edit member form modal
 * TODO merge with add user modal
 */
interface IUserFormModal {
    open: boolean;
    onClose: () => void;
    /**
     * Default values for userForm
     */
    defaultValues: IDefaultUserValues;
    /**
     * User roles options
     */
    possibleUserRoles: PossibleUserRole[];
    /**
     * User id
     */
    userId: number;
    /**
     * If user is editing his profile
     */
    isMe: boolean;
}
const UserFormModal = ({ open, onClose, defaultValues, possibleUserRoles, userId, isMe }: IUserFormModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);

    const { control, handleSubmit, reset } = useForm({ defaultValues, mode: "all" });
    const { dirtyFields } = useFormState({
        control,
    });

    useEffect(() => {
        reset(defaultValues);
    }, [open]);

    const onSubmit: SubmitHandler<IDefaultUserValues> = async (form) => {
        setIsLoading(true);
        await dispatch(updateUser({ userId, req: form }));
        // Updates userData in settings.
        await dispatch(updateUserData({ firstname: form.firstname ?? "", lastname: form.lastname }));
        /**
         * update user role has it own route
         */
        if (dirtyFields?.role) {
            // Format role as "custom:{roleId}" for custom roles
            const rolePayload = form.role.isCustom && form.role.id ? formatCustomRoleId(form.role.id) : form.role.role;
            await dispatch(updateUserRole({ userId, role: rolePayload }));
        }
        onClose();
        setIsLoading(false);
    };

    return (
        <ModalBase modalTitle={t("Members.EditUser")} open={open} onClose={onClose}>
            <form style={formStyle} onSubmit={handleSubmit(onSubmit)}>
                <TextFormComponent label={`${t("Members.Name")}*`} controlName="firstname" control={control} required />
                <TextFormComponent label={t("Members.Surname")} controlName="lastname" control={control} />
                <div>
                    <AutocompleteFormComponent
                        label={t("Members.Role")}
                        control={control}
                        controlName="role"
                        options={possibleUserRoles}
                        renderOptionText={(option) => option.label}
                        disableClearable
                        isOptionEqualToValue={(option, value) => option.role == value.role}
                        disabled={isMe}
                    />
                    {isMe && (
                        <SmallBody color="grey" style={{ marginTop: "0.25rem" }}>
                            {t("Members.BlockRoleChange")}
                        </SmallBody>
                    )}
                    {dirtyFields?.role && (
                        <SmallBody color="info" style={{ marginTop: "0.25rem" }}>
                            {t("Members.ChangeRoleWarning")}
                        </SmallBody>
                    )}
                </div>
                <TextFormComponent label={t("Settings.SignatoryTitle")} controlName="jobTitle" control={control} />
                <StyledButton buttonType="primary" fullWidth type="submit" loading={isLoading}>
                    {t("Members.Validate")}
                </StyledButton>
            </form>
        </ModalBase>
    );
};

export default UserFormModal;
